import { Loader } from "@launchmetrics/lm-ui";
import Breadcrumb from "components/LM/common/Breadcrumb";
import DemographicsDisplay from "components/LM/common/DemographicsDisplay";
import LogDisplay from "components/LM/common/LogDisplay";
import PostsDisplay from "components/LM/common/PostsDisplay";
import ErrorDetails from 'components/LM/SocialMediaProfile/ErrorDetails';
import ProfileDetails from "components/LM/SocialMediaProfile/ProfileDetails";
import 'components/LM/SocialMediaProfile/SocialMediaProfile.scss';
import K2Api from 'lib/ajax/K2Api';
import queryString from 'query-string';
import { useEffect, useState } from "react";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Col, Container, Row } from "reactstrap";

const k2Api = new K2Api();
const DEMOGRAPHICS_SUPPORTED_TYPES = new Set(['instagram', 'tiktok', 'youtube']);

function SocialMediaProfile(props) {
  const { type, typeName } = props;
  const [profile, setProfile] = useState();
  const [lastPostDate, setLastPostDate] = useState();
  const [error, setError] = useState();
  const audienceTranslations = {
    facebook: "fans",
    youtube: "subscribers"
  };

  useEffect(() => {
    async function fetchData() {
      let [key, value] = extractUrlParams();
      try {
        const info = await k2Api.get(`/socialmedia/${type}/info/${key}/${value}`);
        if (!info) return;
        const profile = await retrieveProfileFullInfo(type, info.data.id);
        setProfile({ ...profile, info: info.data });
        const lastPostDate = await retrieveLastPostPublicationDate(type, info.data.id);
        setLastPostDate(lastPostDate);
      } catch (err) {
        console.error(err);
        setError(err);
      }
    }
    fetchData();
  }, [type]);

  if (error) {
    return <ErrorDetails error={error} />;
  } else if (profile) {
    return (
      <div id={type + typeName}>
        <Container fluid className="p-0">
          {breadcrumbPage(type, profile)}
          <Row>
            <Col md="4" xl="3">
              <ProfileDetails type={type} typeName={typeName} profile={profile} audienceLabel={audienceTranslations[type] ? audienceTranslations[type] : 'followers'} lastPostDate={lastPostDate} showExtendedInfo={true} />
            </Col>
            <Col md="8" xl="9">
              <Tabs>
                {DEMOGRAPHICS_SUPPORTED_TYPES.has(type) && (
                  <Tab eventKey="demographicsDisplay" title="Demographics">
                    <DemographicsDisplay profileId={profile.info.id} type={type} />
                  </Tab>
                )}
                <Tab eventKey="logDisplay" title="Audit Logs">
                  <LogDisplay profileId={profile.info.id} type={type} />
                </Tab>
                <Tab eventKey="postsDisplay" title="Posts">
                  <PostsDisplay profileId={profile.info.id} type={type} />
                </Tab>
              </Tabs>
            </Col>
          </Row>
        </Container>
      </div>
    )
  } else {
    return <Loader />;
  }
}

function breadcrumbPage(type, profile) {
  const voiceLink = (profile.voice) ?
    <a href={`/voices/${profile.voice.id}`} className="ml-1">{profile.voice.name}</a> : null;

  const items = [
    voiceLink,
    <a href={profile.info.profile_url} target='_blank' className='external-link' rel='noopener noreferrer'>{profileTag(type, profile.info)}</a>
  ];

  return <Breadcrumb items={items} />;
}

function extractUrlParams() {
  let parsedData = queryString.parse(window.location.search);
  let key;
  let value;
  if (parsedData.screen_name) {
    key = 'screen_name';
    value = parsedData.screen_name
  } else if (parsedData.id) {
    key = 'id';
    value = parsedData.id
  }
  return [key, value];
}

async function retrieveLastPostPublicationDate(type, id) {
  const beginDate = new Date();
  beginDate.setFullYear(beginDate.getFullYear() - 4);
  const profilePosts = await k2Api.get(
    `/socialmedia/${type}/documents/${id}`,
    { params: { beginDate: beginDate, endDate: new Date(), from: 0, size: 1 } }
  );
  if (profilePosts.data.documents.length > 0) {
    const lastPost = profilePosts.data.documents[0];
    const lastPostDate = parseInt(lastPost.id.substring(0, 10), 10);
    return new Date(lastPostDate * 1000);
  } else return;
}

async function retrieveProfileFullInfo(type, id) {

  const crawlingPromise = k2Api.get(`/socialmedia/${type}/crawling/${id}`).catch(
    err => {
      if (err.response.status !== 404) {
        throw new Error("Unexpected error getting crawling information");
      } else {
        return { data: null }
      }
    }
  );

  const voicePromise = k2Api.get(`/socialmedia/${type}/voice/${id}`).catch(
    err => {
      if (err.response.status !== 404) {
        throw new Error("Unexpected error getting voices");
      } else {
        return { data: null }
      }
    }
  );

  const schedulerPromise = k2Api.get(`/socialmedia/${type}/scheduler/${id}`).catch(
    err => {
      if (err.response.status !== 404) {
        throw new Error("Unexpected error getting profile scheduler");
      } else {
        return { data: null }
      }
    }
  );

  const canRecoverPromise = await k2Api.get(`/socialmedia/${type}/recovery/${id}`).catch(
    err => {
      if (err.response.status !== 404) {
        throw new Error("Unexpected error getting can recover profile information");
      } else {
        return { data: null }
      }
    }
  );

  const [crawling, voice, profileScheduler, canRecover] = await Promise.all([crawlingPromise, voicePromise, schedulerPromise, canRecoverPromise]);

  return {
    crawling: crawling.data,
    voice: voice.data,
    scheduler: profileScheduler.data,
    recovery: canRecover.data
  }
}

export function profileTag(type, {screen_name, name, id}) {
  if (['twitter', 'instagram', 'tiktok'].includes(type)) {
    return "@" + screen_name;
  } else if (type === 'facebook') {
    return name;
  } else if (['wechat_public', 'weibo', 'douyin', 'xiaohongshu', 'bilibili'].includes(type)) {
    return "@" + screen_name || name;
  } else {
    return id;
  }
}

export default SocialMediaProfile;
